import { useEffect, useRef } from "react";
import { UseKeepSendingRequestState } from "./types";

const ONE_SECOND_IN_MS = 1000;

export const useKeepSendingRequest = (
	requestFunction: (sendingRequestState: UseKeepSendingRequestState) => any,
	customNumberOfRequests?: number
) => {
	const numberOfRequests = customNumberOfRequests ?? 10;
	const forceStopSendingRequests = useRef<boolean>(false);
	const isSendingRequests = useRef(false);

	const clearHookValues = () => {
		forceStopSendingRequests.current = false;
		isSendingRequests.current = false;
	};

	const waitForTimeout = (timeoutDuration: number) => {
		if (timeoutDuration <= 0) return;

		return new Promise((resolve) =>
			setTimeout(() => {
				resolve(null);
			}, timeoutDuration)
		);
	};

	const get1sTimeoutDuration = (requestStartTimestamp: number): number => {
		const currentTimestamp = performance.now();
		const requestDuration = currentTimestamp - requestStartTimestamp;
		const roundedRequestDuration = Math.round(requestDuration);

		return ONE_SECOND_IN_MS - roundedRequestDuration;
	};

	const keepSendingRequests = async (firstRequestStartTimestamp: number) => {
		isSendingRequests.current = true;

		await waitForTimeout(get1sTimeoutDuration(firstRequestStartTimestamp));

		for (let requestsCounter = 2; requestsCounter <= numberOfRequests; requestsCounter++) {
			if (forceStopSendingRequests.current) break;

			const allRequestsSent = requestsCounter === numberOfRequests;
			const requestStartTimestamp = performance.now();

			await requestFunction({
				allRequestsSent,
				isSendingRequests: isSendingRequests.current,
				forceStopSendingRequests,
			});

			await waitForTimeout(get1sTimeoutDuration(requestStartTimestamp));

			if (allRequestsSent) {
				clearHookValues();
			}
		}
	};

	useEffect(() => {
		return () => {
			forceStopSendingRequests.current = true;
		};
	}, []);

	return { keepSendingRequests };
};
